import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Get token from localStorage
const getToken = () => localStorage.getItem("adminToken");

// Set token in localStorage
const setToken = (token) => localStorage.setItem("adminToken", token);

// Remove token from localStorage
const removeToken = () => localStorage.removeItem("adminToken");

// Create axios instance with auth header
const apiClient = axios.create({
  baseURL: API_URL,
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==================== SERVICES API ====================

export const servicesAPI = {
  getAll: async () => {
    const response = await axios.get(`${API_URL}/services`);
    return response.data;
  },

  create: async (service) => {
    const response = await apiClient.post(`${API_URL}/services`, service);
    return response.data;
  },

  update: async (id, service) => {
    const response = await apiClient.put(`${API_URL}/services/${id}`, service);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`${API_URL}/services/${id}`);
    return response.data;
  },
};

// ==================== PROJECTS API ====================

export const projectsAPI = {
  getAll: async (category = null) => {
    const url = category
      ? `${API_URL}/projects?category=${encodeURIComponent(category)}`
      : `${API_URL}/projects`;
    const response = await axios.get(url);
    return response.data;
  },

  create: async (project) => {
    const response = await apiClient.post(`${API_URL}/projects`, project);
    return response.data;
  },

  update: async (id, project) => {
    const response = await apiClient.put(`${API_URL}/projects/${id}`, project);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`${API_URL}/projects/${id}`);
    return response.data;
  },
};

// ==================== ABOUT API ====================

export const aboutAPI = {
  get: async () => {
    const response = await axios.get(`${API_URL}/about`);
    return response.data;
  },

  update: async (about) => {
    const response = await apiClient.put(`${API_URL}/about`, about);
    return response.data;
  },
};

// ==================== PARTNERS API (Learning Logos) ====================

export const partnersAPI = {
  getAll: async () => {
    const response = await axios.get(`${API_URL}/partners`);
    return response.data;
  },
  create: async (partner) => {
    const response = await apiClient.post(`${API_URL}/partners`, partner);
    return response.data;
  },
  update: async (id, partner) => {
    const response = await apiClient.put(`${API_URL}/partners/${id}`, partner);
    return response.data;
  },
  delete: async (id) => {
    const response = await apiClient.delete(`${API_URL}/partners/${id}`);
    return response.data;
  },
};

// ==================== CERTIFICATES API ====================

export const certificatesAPI = {
  getAll: async () => {
    const response = await axios.get(`${API_URL}/certificates`);
    return response.data;
  },
  create: async (cert) => {
    const response = await apiClient.post(`${API_URL}/certificates`, cert);
    return response.data;
  },
  update: async (id, cert) => {
    const response = await apiClient.put(`${API_URL}/certificates/${id}`, cert);
    return response.data;
  },
  delete: async (id) => {
    const response = await apiClient.delete(`${API_URL}/certificates/${id}`);
    return response.data;
  },
};

// ==================== TESTIMONIALS API ====================

export const testimonialsAPI = {
  getAll: async () => {
    const response = await axios.get(`${API_URL}/testimonials`);
    return response.data;
  },
};

// ==================== CONTACT API ====================

export const contactAPI = {
  submit: async (contact) => {
    const response = await axios.post(`${API_URL}/contact`, contact);
    return response.data;
  },

  getAll: async () => {
    const response = await apiClient.get(`${API_URL}/contacts`);
    return response.data;
  },
};

// ==================== ADMIN/AUTH API ====================

export const adminAPI = {
  login: async (password) => {
    const response = await axios.post(`${API_URL}/admin/login`, { password });
    if (response.data.token) {
      setToken(response.data.token);
    }
    return response.data;
  },

  verify: async () => {
    try {
      const response = await apiClient.post(`${API_URL}/admin/verify`);
      return response.data;
    } catch (error) {
      removeToken();
      throw error;
    }
  },

  logout: () => {
    removeToken();
  },

  isAuthenticated: () => {
    return !!getToken();
  },
};

// Export utility functions
export { getToken, setToken, removeToken };
